import React from 'react'
import { Platform } from 'react-native'

import { yupResolver } from '@hookform/resolvers/yup'
import { FormProvider, useForm } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'

import Button from 'app/components/base/Button'
import Dialog from 'app/components/base/Dialog'
import Stack from 'app/components/base/Stack'
import Text from 'app/components/base/Text'
import SelectField from 'app/components/fields/SelectField'
import TextField from 'app/components/fields/TextField'
import { UploadImageProps } from 'app/components/fields/UploadFields/UploadImage'
import UploadReportImageField from 'app/components/fields/UploadFields/UploadReportImage'
import useFormErrorHandler from 'app/hooks/useFormErrorHandler'
import Alert from 'app/services/Alert'
import { directUploadOne } from 'app/services/Api/activeStorage'
import {
  getReportListingReasons,
  getReportUserReasons
} from 'app/services/Api/user'
import { isErrorHandlerType } from 'app/services/ErrorHandler'
import { useCreateReportMutation } from 'app/services/Query/user/user.mutation'
import { REPORT_TYPE } from 'app/utils/constants/status.constants'
import { CreateListingReportSchema } from 'app/validation/schemas/user.schemas'

import { subtitleMessages, titleMessages } from './ReportDialog.messages'

interface ReportDialogProps {
  open: boolean
  onClose: () => void
  id: string
  type: ReportType
  onSuccess?: () => void
  onError?: () => void
}

const getReasonOptions = {
  listing: getReportListingReasons,
  user: getReportUserReasons
}

const ReportDialog: RFC<ReportDialogProps> = ({
  open,
  onClose,
  id,
  type,
  onError,
  onSuccess
}) => {
  const intl = useIntl()

  const methods = useForm<CreateReportFV>({
    defaultValues: {
      main_reason: '',
      alternative_reason: ''
    },
    resolver: yupResolver(CreateListingReportSchema),
    mode: 'all'
  })
  const handleFormError = useFormErrorHandler(methods.setError)

  const { mutateAsync } = useCreateReportMutation()

  const { isSubmitting, isDirty } = methods.formState

  const onSubmit = async (values: CreateReportFV) => {
    try {
      await mutateAsync({
        ...values,
        reportable_id: id,
        reportable_type: REPORT_TYPE[type]
      })

      Alert.success(
        intl.formatMessage({
          defaultMessage: 'Thank you for your contribution!'
        })
      )

      onClose()

      if (onSuccess) {
        onSuccess()
      }
    } catch (error) {
      if (isErrorHandlerType(error)) {
        handleFormError(error?.data)
      }

      if (onError) {
        onError()
      }
    }
  }

  return (
    <Dialog
      open={open}
      titleSx={{ alignSelf: 'center', mb: 2.5 }}
      dialogSx={{
        ...Platform.select({
          web: {
            '& .MuiDialog-paper': {
              py: 5,
              px: { xs: 2, md: 6.25 },
              maxWidth: '527px',
              width: '100%'
            },
            '& .MuiDialogContentText-root': {
              color: 'inherit'
            }
          },
          android: {
            minHeight: 495
          }
        })
      }}
      title={<FormattedMessage {...titleMessages[type]} />}
      onClose={onClose}
      actions={
        <Stack direction="column" sx={{ width: '100%', alignItems: 'center' }}>
          <Button
            size="small"
            onClick={methods.handleSubmit(onSubmit)}
            loading={isSubmitting}
            disabled={!isDirty}
            sx={{ width: '100%', maxWidth: 352 }}
          >
            <FormattedMessage defaultMessage="Submit" />
          </Button>
        </Stack>
      }
      contentSx={{ ...Platform.select({ web: { mb: 2 } }) }}
      actionsSx={{ ...Platform.select({ native: { mb: 2 } }) }}
    >
      <FormProvider {...methods}>
        <Stack
          direction="column"
          spacing={Platform.select({ web: 3, native: 2 })}
        >
          <Text
            variant="body2"
            sx={{ textAlign: 'center', color: 'text.third' }}
          >
            <FormattedMessage {...subtitleMessages[type]} />
          </Text>

          <Stack
            direction="column"
            spacing={2}
            sx={{ width: '100%', alignItems: 'center' }}
          >
            <SelectField
              name="main_reason"
              optionSettings={{
                key: 'main_reason',
                loadOptions: getReasonOptions[type],
                optionValue: 'text',
                optionLabel: 'text'
              }}
              label={intl.formatMessage({
                defaultMessage: 'Reason for Report'
              })}
              formControlProps={{
                sx: { maxWidth: 352 }
              }}
            />

            <TextField
              name="alternative_reason"
              label={intl.formatMessage({
                defaultMessage: 'Please, specify the reason'
              })}
              multiline
              formControlProps={{
                sx: { maxWidth: 352 }
              }}
              {...Platform.select({
                web: {
                  row: 3
                },
                native: {
                  sx: {
                    maxHeight: 130,
                    height: '100%'
                  }
                }
              })}
            />

            <UploadReportImageField
              name="attachments_attributes"
              maxSize={1024 * 1024 * 10}
              FormControlProps={{
                sx: { maxWidth: 352 }
              }}
              accept={
                Platform.select({
                  web: {
                    'image/*': ['.jpg', '.jpeg', '.png', '.heic']
                  },
                  native: ['jpg', 'jpeg', 'png', 'heic']
                }) as UploadImageProps['accept']
              }
              uploadFile={(file) => directUploadOne(file)}
            />
          </Stack>
        </Stack>
      </FormProvider>
    </Dialog>
  )
}

export default ReportDialog
